import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Check, ChevronDown, Lock } from "lucide-react";

/**
 * Workspace switcher — root admin only. Moves the session between the demo
 * lane and the Paradox Unlimited business lane. The server decides who may
 * switch and where to; this only renders what it is told.
 */
export default function WorkspaceSwitcher() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/workspaces"); setData(data); }
    catch { setData(null); }
  };
  useEffect(() => { load(); }, []);

  const go = async (companyId) => {
    setBusy(true);
    try {
      const { data: out } = await api.post("/workspaces/switch", { company_id: companyId });
      toast.success(`Switched to ${out.name}`);
      window.location.reload();     // re-fetch every scoped view cleanly
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  if (!data) return null;
  const active = data.active || {};

  // Locked accounts (the reviewer) get a static badge, never a menu.
  if (!data.can_switch) {
    if (!active.lane) return null;
    return (
      <div data-testid="workspace-badge"
           className="hidden sm:flex items-center gap-2 px-3 py-1.5 border-2 font-mono text-[10px] uppercase tracking-widest"
           style={{ background: active.color, color: active.on_color, borderColor: active.color }}>
        {data.locked ? <Lock className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
        <span data-testid="workspace-badge-label">{active.label}</span>
      </div>
    );
  }

  return (
    <div className="relative flex items-center gap-2">
      <span data-testid="workspace-switcher-label"
            className="hidden md:inline font-mono text-[10px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
        Switch Workspace:
      </span>
      <button data-testid="workspace-switcher-btn" onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 border-2 font-mono text-[10px] uppercase tracking-widest"
        style={{ background: active.color || "#0A0A0A", color: active.on_color || "#fff",
                 borderColor: active.color || "#0A0A0A" }}>
        <Building2 className="w-3 h-3" />
        <span data-testid="workspace-active-label" className="max-w-[160px] truncate">
          {active.label || active.company_name || "Workspace"}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div data-testid="workspace-menu"
             className="absolute right-0 top-full mt-2 w-72 bg-white border-2 border-ink shadow-hard z-50">
          <div className="px-3 py-2 border-b-2 border-ink overline">Switch workspace</div>
          {data.workspaces.map((w) => (
            <button key={w.company_id} data-testid={`workspace-option-${w.company_id}`}
              data-lane={w.lane || "other"}
              disabled={busy || w.active || w.locked}
              onClick={() => go(w.company_id)}
              className="w-full text-left px-3 py-3 border-b border-ink/10 hover:bg-secondary disabled:opacity-60 flex items-start gap-2">
              <span className="w-3 h-3 mt-1 shrink-0 border border-ink"
                    style={{ background: w.color || "#fff" }} />
              <span className="flex-1">
                <span className="block font-mono text-xs font-black">{w.name}</span>
                <span className="block font-mono text-[10px] text-muted-foreground">
                  {w.sublabel || w.role}
                  {w.reviewer_mode && " · no live money"}
                  {w.locked && " · locked"}
                </span>
              </span>
              {w.active && <Check className="w-4 h-4 mt-0.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
